/*
PROV: COHINT037.BF.UICONTROLS
REQ: AUD-UX-10, AUD-UX-11, UX-BASE-05, SYS-ARCH-15
WHY: Shared form controls (shadcn pattern) with consistent focus/validation styling.
*/

import React, { useRef, useCallback } from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, X } from "lucide-react";
import { cn } from "./lib/utils";

// -- shared dark focus pattern --
const FOCUS_DARK = "dark:focus-visible:border-brand-600 dark:focus-visible:ring-brand-600/50";

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function Input({ className, invalid, type, ...props }, ref) {
  return (
    <input
      type={type}
      ref={ref}
      aria-invalid={invalid ? "true" : props["aria-invalid"]}
      className={cn(
        "m-0 box-border h-9 w-full min-w-0 rounded-md border border-brand-200 bg-transparent px-3 py-1 text-base text-brand-900 shadow-sm transition-[color,box-shadow] outline-none",
        "dark:border-brand-700 dark:bg-brand-900 dark:text-brand-100",
        "file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-brand-900 dark:file:text-brand-100",
        "placeholder:text-brand-400 dark:placeholder:text-brand-500",
        "focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50",
        FOCUS_DARK,
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        "md:text-sm",
        invalid && "border-error-500 ring-error-300/20",
        className
      )}
      {...props}
    />
  );
});

// ---------------------------------------------------------------------------
// TextArea
// ---------------------------------------------------------------------------

const TextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }
>(function TextArea({ className, invalid, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid ? "true" : props["aria-invalid"]}
      className={cn(
        "m-0 box-border min-h-16 w-full rounded-md border border-brand-200 bg-transparent px-3 py-2 text-base text-brand-900 shadow-sm transition-[color,box-shadow] outline-none resize-y",
        "dark:border-brand-700 dark:bg-brand-900 dark:text-brand-100",
        "placeholder:text-brand-400 dark:placeholder:text-brand-500",
        "focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50",
        FOCUS_DARK,
        "disabled:cursor-not-allowed disabled:opacity-50",
        "md:text-sm",
        invalid && "border-error-500 ring-error-300/20",
        className
      )}
      {...props}
    />
  );
});

// ---------------------------------------------------------------------------
// Label (Radix)
// ---------------------------------------------------------------------------

const Label = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(function Label({ className, ...props }, ref) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(
        "flex items-center gap-2 text-sm leading-none font-medium select-none text-brand-900 dark:text-brand-100 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
        className
      )}
      {...props}
    />
  );
});

// ---------------------------------------------------------------------------
// Select (native)
// ---------------------------------------------------------------------------

const Select = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(function Select({ className, invalid, children, ...props }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid ? "true" : props["aria-invalid"]}
      className={cn(
        "m-0 box-border h-9 w-full appearance-none rounded-md border border-brand-200 bg-transparent px-3 py-1 pr-8 text-base text-brand-900 shadow-sm transition-[color,box-shadow] outline-none",
        "dark:border-brand-700 dark:bg-brand-900 dark:text-brand-100",
        "focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50",
        FOCUS_DARK,
        "disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
        "md:text-sm",
        invalid && "border-error-500 ring-error-300/20",
        className
      )}
      {...props}
    >
      {children}
    </select>
  );
});

// ---------------------------------------------------------------------------
// Checkbox (Radix — new)
// ---------------------------------------------------------------------------

const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(function Checkbox({ className, ...props }, ref) {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        "peer box-border size-4 shrink-0 rounded-[4px] border border-brand-200 shadow-sm transition-shadow outline-none",
        "dark:border-brand-700",
        "focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50",
        FOCUS_DARK,
        "disabled:cursor-not-allowed disabled:opacity-50",
        "data-[state=checked]:border-brand-900 data-[state=checked]:bg-brand-900 data-[state=checked]:text-white",
        "dark:data-[state=checked]:border-brand-50 dark:data-[state=checked]:bg-brand-50 dark:data-[state=checked]:text-brand-900",
        className
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="grid place-content-center text-current transition-none">
        <Check className="size-3.5" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

// ---------------------------------------------------------------------------
// CheckboxInput (backwards-compat native checkbox)
// ---------------------------------------------------------------------------

function CheckboxInput({
  className,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { className?: string }) {
  return (
    <input
      {...props}
      type="checkbox"
      className={cn(
        "m-0 box-border h-4 w-4 rounded border border-brand-300 text-brand-900",
        "dark:border-brand-600 dark:text-brand-100",
        "focus:outline-none focus:ring-2 focus:ring-brand-100 focus:ring-offset-2 focus:ring-offset-white",
        "dark:focus:ring-brand-800 dark:focus:ring-offset-brand-950",
        className
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// RadioInput
// ---------------------------------------------------------------------------

function RadioInput({
  className,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { className?: string }) {
  return (
    <input
      {...props}
      type="radio"
      className={cn(
        "m-0 box-border h-4 w-4 border border-brand-300 text-brand-900",
        "dark:border-brand-600 dark:text-brand-100",
        "focus:outline-none focus:ring-2 focus:ring-brand-100 focus:ring-offset-2 focus:ring-offset-white",
        "dark:focus:ring-brand-800 dark:focus:ring-offset-brand-950",
        className
      )}
    />
  );
}

// ---------------------------------------------------------------------------
// Helper / Error text
// ---------------------------------------------------------------------------

function HelperText({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { className?: string }) {
  return <div {...props} className={cn("text-xs text-brand-600 dark:text-brand-400", className)} />;
}

function ErrorText({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { className?: string }) {
  return <div {...props} className={cn("text-xs text-error-700 dark:text-error-400", className)} />;
}

// ---------------------------------------------------------------------------
// OtpInput
// ---------------------------------------------------------------------------

const OTP_LENGTH = 6;

const OTP_DIGIT =
  "w-10 h-12 box-border font-sans text-lg font-semibold text-center bg-transparent text-brand-900 dark:text-brand-100 border border-brand-200 dark:border-brand-700 dark:bg-brand-900 rounded-md shadow-sm outline-none focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50 dark:focus-visible:border-brand-600 dark:focus-visible:ring-brand-600/50";

const OTP_DIGIT_INVALID =
  "border-error-500 focus-visible:ring-error-300/20";

type OtpInputProps = {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  autoFocus?: boolean;
  className?: string;
};

function OtpInput({ value, onChange, invalid, autoFocus, className }: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.split("").slice(0, OTP_LENGTH);
  while (digits.length < OTP_LENGTH) digits.push("");

  const setDigit = useCallback(
    (index: number, char: string) => {
      const next = [...digits];
      next[index] = char;
      onChange(next.join(""));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value, onChange],
  );

  const focusIndex = useCallback((i: number) => {
    inputRefs.current[i]?.focus();
  }, []);

  const handleInput = useCallback(
    (index: number, e: React.FormEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const char = input.value.replace(/\D/g, "").slice(-1);
      setDigit(index, char);
      if (char && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
      }
    },
    [setDigit, focusIndex],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        if (digits[index]) {
          setDigit(index, "");
        } else if (index > 0) {
          setDigit(index - 1, "");
          focusIndex(index - 1);
        }
        e.preventDefault();
      } else if (e.key === "ArrowLeft" && index > 0) {
        focusIndex(index - 1);
        e.preventDefault();
      } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
        focusIndex(index + 1);
        e.preventDefault();
      }
    },
    [digits, setDigit, focusIndex],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
      if (pasted) {
        onChange(pasted.padEnd(OTP_LENGTH, "").slice(0, OTP_LENGTH));
        focusIndex(Math.min(pasted.length, OTP_LENGTH - 1));
      }
    },
    [onChange, focusIndex],
  );

  return (
    <div className={cn("flex gap-2", className)} onPaste={handlePaste}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { inputRefs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          autoFocus={autoFocus && i === 0}
          maxLength={2}
          value={digit}
          onInput={(e) => handleInput(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
          className={cn(OTP_DIGIT, invalid && OTP_DIGIT_INVALID)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverlayCloseButton
// ---------------------------------------------------------------------------

function OverlayCloseButton({
  className,
  "aria-label": ariaLabel = "Close",
  variant = "boxed",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { className?: string; variant?: "boxed" | "plain" }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      {...props}
      className={cn(
        variant === "plain"
          ? "box-border h-6 w-6 rounded-none border-0 bg-transparent p-0 text-brand-900 dark:text-brand-100"
          : "box-border size-9 rounded-md border border-brand-200 bg-white text-brand-900 shadow-sm dark:border-brand-700 dark:bg-brand-900 dark:text-brand-100",
        "inline-flex items-center justify-center shrink-0",
        "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
        "outline-none focus-visible:border-brand-300 focus-visible:ring-[3px] focus-visible:ring-brand-300/50",
        "dark:focus-visible:border-brand-600 dark:focus-visible:ring-brand-600/50",
        className
      )}
    >
      <X size={18} strokeWidth={2.5} aria-hidden="true" />
    </button>
  );
}

export {
  Input,
  TextArea,
  Label,
  Select,
  Checkbox,
  CheckboxInput,
  RadioInput,
  HelperText,
  ErrorText,
  OtpInput,
  OverlayCloseButton,
};
